import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import './DashboardWastageApprovals.css';
import { onDataChanged, onScreenResumed } from '../utils/dataChangeSignal';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BASE_URL = (API_BASE_URL || 'http://localhost:5000').replace(/\/api\/?$/, '');

const DashboardWastageApprovals = ({ goTo }) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [reviewNotes, setReviewNotes] = useState({});
  const [userId, setUserId] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [fullImageModal, setFullImageModal] = useState({ open: false, url: '', itemName: '' });
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  const fetchPendingApprovals = useCallback(async (uid, bid) => {
    try {
      const url = `${BASE_URL}/api/wastage/threshold-approvals/pending?userId=${uid}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'X-Business-Id': String(bid) }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setApprovals(result.data);
        }
      }
    } catch (error) {
      console.error('[DashboardWastageApprovals] Error fetching:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) { setLoading(false); return; }
        const user = JSON.parse(userStr);
        const uid = user.id || user.user_id || user.userId;
        const bid = localStorage.getItem('businessId') || user.business_id || user.businessId;
        setUserId(uid);
        setBusinessId(bid);

        // Check role
        const baseUrl = BASE_URL;
        const resp = await fetch(`${baseUrl}/api/users/${uid}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          const role = (data.data?.role_name || '').toLowerCase();
          if (role.includes('owner') || role.includes('admin')) {
            setIsOwner(true);
            await fetchPendingApprovals(uid, bid);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.error('[DashboardWastageApprovals] init error:', e);
        setLoading(false);
      }
    };
    init();
    // Identify the user once. This effect used to also carry the refresh timer,
    // which put userId/businessId/isOwner in its dependency list — so setting
    // them from init() re-ran init() and fetched the user's role a second time.
  }, [fetchPendingApprovals]);

  // Owner's pending wastage approvals, refreshed on events rather than polled.
  //
  // This sat on the dashboard asking every 30 seconds whether a staff member had
  // submitted an over-threshold wastage entry — around 2,900 requests a day to
  // catch something that happens a few times a week.
  //
  // A submission made in this browser announces itself; one made on a staff
  // member's own device is picked up when the owner comes back to the dashboard
  // tab. Approving or rejecting updates the list directly, as before.
  useEffect(() => {
    if (!(userId && businessId && isOwner)) return undefined;

    const refresh = () => { fetchPendingApprovals(userId, businessId); };
    const stopListening = onDataChanged(refresh);
    const stopResuming = onScreenResumed(refresh);

    return () => {
      stopListening();
      stopResuming();
    };
  }, [fetchPendingApprovals, userId, businessId, isOwner]);

  const handleApprove = async (approvalId) => {
    setProcessingId(approvalId);
    try {
      const response = await fetch(
        `${BASE_URL}/api/wastage/threshold-approvals/${approvalId}/approve`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json', 'X-Business-Id': String(businessId) },
          body: JSON.stringify({
            userId,
            review_notes: reviewNotes[approvalId] || 'Approved from dashboard'
          })
        }
      );
      if (response.ok) {
        setApprovals(prev => prev.filter(r => r.approval_id !== approvalId));
        setExpandedId(null);
      } else {
        const result = await response.json();
        alert(result.error || 'Failed to approve');
      }
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (approvalId) => {
    const notes = reviewNotes[approvalId];
    if (!notes?.trim()) {
      alert('Please add review notes before rejecting.');
      return;
    }
    setProcessingId(approvalId);
    try {
      const response = await fetch(
        `${BASE_URL}/api/wastage/threshold-approvals/${approvalId}/reject`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json', 'X-Business-Id': String(businessId) },
          body: JSON.stringify({ userId, review_notes: notes })
        }
      );
      if (response.ok) {
        setApprovals(prev => prev.filter(r => r.approval_id !== approvalId));
        setExpandedId(null);
      } else {
        const result = await response.json();
        alert(result.error || 'Failed to reject');
      }
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setProcessingId(null);
    }
  };

  // Don't render for non-owners or when loading
  if (!isOwner || loading) return null;
  // Don't render if no pending approvals
  if (approvals.length === 0) return null;

  return (
    <>
      <div className="dwa-card">
        <div className="dwa-header">
          <div className="dwa-header-left">
            <h3 className="dwa-title">Wastage Approvals</h3>
            <span className="dwa-badge">({approvals.length})</span>
          </div>
          <button className="dwa-view-all" onClick={() => goTo('wastage-threshold-approvals')}>
            View All
          </button>
        </div>

        <div className="dwa-list">
          {approvals.slice(0, 5).map((req) => {
            const isExpanded = expandedId === req.approval_id;
            const displayTime = new Date(req.created_at).toLocaleString([], {
              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true
            });

            // Parse justification for reason
            const justification = String(req.justification || '');
            const reasonMatch = justification.match(/Reason:\s*(.+)/i);
            let reasonText = reasonMatch ? reasonMatch[1].trim() : '';
            if (!reasonText || /^waste recorded via/i.test(reasonText) || reasonText === 'Not specified') {
              reasonText = '';
            }

            // Determine threshold exceeded status
            const thresholdExceeded = req.current_threshold && 
              parseFloat(req.requested_threshold) > parseFloat(req.current_threshold);

            const hasImage = !!(req.evidence_image_id || req.evidence_image_url);

            return (
              <div
                key={req.approval_id}
                className={`dwa-item ${isExpanded ? 'dwa-item-expanded' : ''} ${thresholdExceeded ? 'dwa-item-exceeded' : ''}`}
              >
                {/* Compact row */}
                <div className="dwa-item-row" onClick={() => setExpandedId(isExpanded ? null : req.approval_id)}>
                  <div className="dwa-item-info">
                    <div className="dwa-item-name">
                      
                      {req.item_name}
                    </div>
                    <div className="dwa-item-meta">
                      <span className="dwa-item-by">{req.requested_by_name}</span>
                      <span className="dwa-item-sep">·</span>
                      <span className="dwa-item-qty">{parseInt(req.requested_threshold)} {req.threshold_unit || 'plates'}</span>
                      {req.current_threshold && (
                        <>
                          <span className="dwa-item-sep">·</span>
                          <span className="dwa-item-threshold">Limit: {parseInt(req.current_threshold)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="dwa-item-actions-compact">
                    <span className="dwa-item-time">{displayTime}</span>
                    <span className="dwa-expand-icon">
                      <FontAwesomeIcon icon={isExpanded ? faAngleUp : faAngleDown} />
                    </span>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="dwa-item-detail">
                    {/* Evidence image */}
                    {hasImage && (
                      <div className="dwa-image-section">
                        {imageLoadErrors[req.approval_id] ? (
                          <div className="dwa-image-error">📸 Image not accessible</div>
                        ) : (
                          <img
                            src={
                              req.evidence_image_id
                                ? `${BASE_URL}/api/wastage/photos/proxy/${req.evidence_image_id}`
                                : (req.evidence_image_url?.startsWith('http')
                                  ? req.evidence_image_url
                                  : `${BASE_URL}${req.evidence_image_url}`)
                            }
                            alt={`Evidence for ${req.item_name}`}
                            className="dwa-evidence-img"
                            onClick={() => {
                              const imageUrl = req.evidence_image_id
                                ? `${BASE_URL}/api/wastage/photos/proxy/${req.evidence_image_id}`
                                : (req.evidence_image_url?.startsWith('http')
                                  ? req.evidence_image_url
                                  : `${BASE_URL}${req.evidence_image_url}`);
                              setFullImageModal({ open: true, url: imageUrl, itemName: req.item_name });
                            }}
                            onLoad={() => {
                              setImageLoadErrors(prev => {
                                if (!prev[req.approval_id]) return prev;
                                const next = { ...prev };
                                delete next[req.approval_id];
                                return next;
                              });
                            }}
                            onError={() => setImageLoadErrors(prev => ({ ...prev, [req.approval_id]: true }))}
                          />
                        )}
                      </div>
                    )}

                    {/* Detail info */}
                    <div className="dwa-detail-info">
                      <div className="dwa-detail-row">
                        <span className="dwa-label">Recorded by:</span>
                        <span>{req.requested_by_name}</span>
                      </div>
                      <div className="dwa-detail-row">
                        <span className="dwa-label">Quantity wasted:</span>
                        <span className="dwa-value-danger">{parseInt(req.requested_threshold)} {req.threshold_unit || 'plates'}</span>
                      </div>
                      {req.current_threshold && (
                        <div className="dwa-detail-row">
                          <span className="dwa-label">Threshold limit:</span>
                          <span>{parseInt(req.current_threshold)} {req.threshold_unit || 'plates'}</span>
                        </div>
                      )}
                      {reasonText && (
                        <div className="dwa-detail-row">
                          <span className="dwa-label">Reason:</span>
                          <span>{reasonText}</span>
                        </div>
                      )}
                    </div>

                    {/* Review notes */}
                    <textarea
                      className="dwa-notes-input"
                      placeholder="Add review notes..."
                      value={reviewNotes[req.approval_id] || ''}
                      onChange={(e) => setReviewNotes(prev => ({ ...prev, [req.approval_id]: e.target.value }))}
                      disabled={processingId === req.approval_id}
                      rows={2}
                    />

                    {/* Action buttons */}
                    <div className="dwa-action-buttons">
                      <button
                        className="dwa-btn-reject"
                        onClick={() => handleReject(req.approval_id)}
                        disabled={processingId === req.approval_id}
                      >
                        {processingId === req.approval_id ? 'Processing...' : 'Reject'}
                      </button>
                      <button
                        className="dwa-btn-approve"
                        onClick={() => handleApprove(req.approval_id)}
                        disabled={processingId === req.approval_id}
                      >
                        {processingId === req.approval_id ? 'Processing...' : 'Approve'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {approvals.length > 5 && (
            <div className="dwa-more">
              and {approvals.length - 5} more approvals...
            </div>
          )}
        </div>
      </div>

      {/* Full Image Modal */}
      {fullImageModal.open && (
        <div
          className="dwa-image-modal-overlay"
          onClick={() => setFullImageModal({ open: false, url: '', itemName: '' })}
        >
          <button
            className="dwa-image-modal-close"
            onClick={(e) => { e.stopPropagation(); setFullImageModal({ open: false, url: '', itemName: '' }); }}
          >
            ✕
          </button>
          <img
            src={fullImageModal.url}
            alt={`Full size evidence for ${fullImageModal.itemName}`}
            className="dwa-image-modal-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default DashboardWastageApprovals;
